<template>
  <div class="profile-container">
    <AppHeader title="Mijn Profiel" :show-back-button="true" back-to="/" back-text="Terug naar dashboard" />

    <SaveAnimation :show="showSaveAnimation" :success="saveSuccess" />

    <div class="profile-content">
      <div v-if="loading" class="loading">
        Gegevens laden...
      </div>
      <div v-else-if="error" class="error">
        {{ error }}
      </div>
      <form v-else @submit.prevent="handleSubmit" class="profile-form">
        <div class="form-section">
          <h2>Bedrijfsgegevens</h2>
          <div class="form-group">
            <InputField label="Bedrijfsnaam" v-model="form.naam" required />
          </div>

          <div class="form-group">
            <InputField label="Contactpersoon" v-model="form.contactnaam" required />
          </div>
        </div>

        <div class="form-section">
          <h2>Adresgegevens</h2>
          <div class="form-row">
            <div class="form-group">
              <InputField label="Straat" v-model="form.straat" required />
            </div>
            <div class="form-group small">
              <InputField label="Huisnummer" v-model="form.huisnr" required />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <InputField label="Postcode" v-model="form.postcode" required />
            </div>
            <div class="form-group">
              <InputField label="Plaats" v-model="form.plaats" required />
            </div>
          </div>
        </div>

        <div class="form-section">
          <h2>Contactgegevens</h2>
          <div class="form-group">
            <InputField label="Telefoonnummer" v-model="form.telnr" type="tel" required />
          </div>
        </div>

        <div class="form-actions">
          <Button
            type="submit"
            label="Opslaan"
            icon="pi pi-check"
            :loading="loading"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { useOpdrachtgeverStore } from '../stores/opdrachtgever'
  import { useAuthStore } from '../stores/auth'
  import { useToast } from '../composables/useToast'
  import AppHeader from '../components/AppHeader.vue'
  import SaveAnimation from '../components/SaveAnimation.vue'
  import InputField from '../components/InputField.vue'
  import Button from 'primevue/button'

  const opdrachtgeverStore = useOpdrachtgeverStore()
  const authStore = useAuthStore()
  const toast = useToast()

  const loading = ref(false)
  const error = ref<string | null>(null)
  const showSaveAnimation = ref(false)
  const saveSuccess = ref(false)
  const form = ref({
    naam: '',
    contactnaam: '',
    straat: '',
    huisnr: '',
    postcode: '',
    plaats: '',
    telnr: ''
  })

  const loadProfile = async () => {
    try {
      loading.value = true
      if (!authStore.user) return
      const data = await opdrachtgeverStore.getOpdrachtgeverById(authStore.user.id)
      if (data) {
        form.value = {
          naam: data.naam || '',
          contactnaam: data.contactnaam || '',
          straat: data.straat || '',
          huisnr: data.huisnr || '',
          postcode: data.postcode || '',
          plaats: data.plaats || '',
          telnr: data.telnr || ''
        }
      }
    } catch (err) {
      error.value = 'Er is een fout opgetreden bij het laden van uw gegevens.'
      console.error('Error loading profile:', err)
    } finally {
      loading.value = false
    }
  }

  const handleSubmit = async () => {
    try {
      loading.value = true
      showSaveAnimation.value = true
      saveSuccess.value = false

      if (!authStore.user) return
      await opdrachtgeverStore.updateOpdrachtgever(authStore.user.id, form.value)

      saveSuccess.value = true
      setTimeout(() => {
        showSaveAnimation.value = false
      }, 2000)
      toast.success('Uw gegevens zijn succesvol opgeslagen')
    } catch (err) {
      error.value = 'Er is een fout opgetreden bij het opslaan van uw gegevens.'
      console.error('Error saving profile:', err)
      toast.error('Er is een fout opgetreden bij het opslaan van uw gegevens')
    } finally {
      loading.value = false
    }
  }

  onMounted(() => {
    loadProfile()
  })
</script>

<style scoped>
  /* Colours come from the PrimeVue preset (mytheme.ts), mirroring the login
     card, so the page follows the portal's light/dark mode. */
  .profile-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 2rem;
  }

  .profile-content {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 4px;
    box-shadow: var(--shadow-sm);
    padding: 2rem;
    margin-top: 2rem;
  }

  .profile-form {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .form-section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--p-background-800);
    margin-bottom: 0.5rem;
  }

  .form-row {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 1rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-group.small {
    max-width: 120px;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
  }

  .loading,
  .error {
    text-align: center;
    padding: 2rem;
    color: var(--p-background-600);
  }

  .error {
    color: var(--color-error);
  }
</style>
